import { cloneDeep, get, set } from 'lodash';
import * as Yup from 'yup';
import {
    getExactLengthValidation,
    getFloatValidation,
    getMaxValidation,
} from 'views/declarations/utils/validation-utils';
import { IrelandImportDeclaration } from 'store/declarations/ireland/import-declaration';

export type Box44Structure = {
    documentType?: string;
    documentIdentifier?: string;
    monetaryAmount?: string;
    measurement?: string;
};

export const makeTransformation = (declaration: any, ...transformationFunctions: ((declaration: any) => any)[]) => {
    let _declaration = cloneDeep(declaration);
    transformationFunctions.forEach((func) => {
        _declaration = func(_declaration);
    });
    return _declaration;
};

export const getBox44Key = (entry: any, structure: Box44Structure): string | undefined => {
    const keyFields = [structure.documentType, structure.measurement, structure.monetaryAmount].filter(
        (v): v is string => Boolean(v)
    );

    const matchedField = keyFields.find((field) => {
        const value = entry[field];
        return value !== undefined && value !== null && value !== '';
    });

    return matchedField ? String(entry[matchedField]) : undefined;
};

const IrelandImportDeclarationUtils = {
    autofillImporterDeclarant: (declaration: any | undefined) => {
        if (declaration?.declarant) {
            const { id, eori, statusCode, ...address } = declaration.declarant;
            if (eori) declaration.declarant = { id, statusCode, eori };
            else declaration.declarant = { id, statusCode, ...address };
        }
        if (declaration?.goodsShipment?.importer) {
            const { id, eori, statusCode, ...address } = declaration.goodsShipment.importer;
            if (eori) declaration.goodsShipment.importer = { id, statusCode, eori };
            else declaration.goodsShipment.importer = { id, statusCode, ...address };
        }
        return declaration;
    },
    box44ToObject: (box44: any[] | undefined, structure: Box44Structure) => {
        if (!Array.isArray(box44)) return box44;
        return box44.reduce((acc, entry) => {
            const key = getBox44Key(entry, structure);
            if (!key) return acc;

            acc[key] = entry;
            return acc;
        }, {} as Record<string, any>);
    },
    box44ToArray: (box44: Record<string, Box44Structure> | undefined) => {
        if (!box44 || !Object.keys(box44).length) return [];
        if (Array.isArray(box44)) return box44;

        return Object.values(box44);
    },
    box44Transform: (func: Function, box44Path: string, box44KeyNames: any, data: any) => {
        const box44 = get(data, box44Path);
        const newBox44 = func(box44, box44KeyNames);
        return set(data, box44Path, newBox44);
    },
    additionsAndDeductions: (declaration: IrelandImportDeclaration) => {
        const additionsDeductions = declaration.goodsShipment?.additionsDeductions;

        additionsDeductions?.forEach((additionDeduction: any) => {
            const float = parseFloat(additionDeduction.amount);

            if (isNaN(float)) additionDeduction.amount = null;
            else additionDeduction.amount = float.toFixed(2);
        });

        return declaration;
    },
};

export default IrelandImportDeclarationUtils;

// H1-H7 have the same validation rules for uploaded documents and only the document type and identifier are different
export const getDocumentsValidationSchema = ({
    documentTypeKey,
    documentIdentifierKey,
    measurementKey,
    monetaryAmountKey,
}: {
    documentTypeKey: string;
    documentIdentifierKey: string;
    measurementKey: string;
    monetaryAmountKey: string;
}) => {
    return Yup.object({
        [documentTypeKey]: Yup.string()
            .nullable()
            .template()
            .test(
                'document-type-required-when-identifier-exists',
                'Document type is required when document identifier is provided.',
                function (value) {
                    const documentIdentifier = this.parent?.[documentIdentifierKey];

                    if (documentIdentifier) {
                        return !!value;
                    }

                    return true;
                }
            ),
        [documentIdentifierKey]: Yup.string()
            .nullable()
            .template()
            .test(
                'document-identifier-required-when-type-exists',
                'Document identifier is required when document type is provided.',
                function (value) {
                    const documentType = this.parent?.[documentTypeKey];

                    if (documentType) {
                        return !!value;
                    }

                    return true;
                }
            )
            .test('n935-min-length', 'Invoice must be at least 5 characters long.', function (value) {
                const documentType = this.parent?.[documentTypeKey];

                if (documentType === 'N935') {
                    if (!value) return false;
                    return value.trim().length >= 5;
                }

                return true;
            }),
        authorityNameSubmitter: getMaxValidation('authorityNameSubmitter', 70).nullable().template(),
        date: Yup.date().nullable().template(),
        [measurementKey]: getMaxValidation(measurementKey, 6)
            .nullable()
            .template()
            .test(
                'measurement-required-when-quantity-exists',
                'Measurement unit is required when quantity is provided.',
                function (value) {
                    const quantity = this.parent?.quantity;

                    if (quantity !== undefined && quantity !== null && quantity !== '') {
                        return !!value;
                    }

                    return true;
                }
            ),
        quantity: getFloatValidation('quantity', 16, 6)
            .nullable()
            .template()
            .test(
                'quantity-required-when-measurement-exists',
                'Quantity is required when measurement unit is selected.',
                function (value) {
                    const measurement = this.parent?.[measurementKey];

                    if (measurement) {
                        return value !== undefined && value !== null;
                    }

                    return true;
                }
            ),
        [monetaryAmountKey]: getExactLengthValidation(monetaryAmountKey, 3)
            .nullable()
            .template()
            .test(
                'currency-required-when-amount-exists',
                'Currency is required when amount is provided.',
                function (value) {
                    const amount = this.parent?.amount;

                    if (amount !== undefined && amount !== null && amount !== '') {
                        return !!value;
                    }

                    return true;
                }
            ),
        amount: getFloatValidation('amount', 16, 2)
            .nullable()
            .template()
            .test(
                'amount-required-when-currency-exists',
                'Amount is required when currency is selected.',
                function (value) {
                    const currency = this.parent?.[monetaryAmountKey];

                    if (currency) {
                        return value !== undefined && value !== null;
                    }

                    return true;
                }
            ),
    });
};
